import { baseApiService } from "./baseApiService";

// User interfaces
export interface UserResponse {
  id: string;
  roleId: string;
  roleName: string;
  companyId: string;
  companyName: string;
  userName: string;
  email: string;
  phoneNumber?: string;
  isActive?: boolean;
}

export interface UpdateUserStatusDto {
  isActive: boolean;
}

export interface CreateUserDto {
  // userId: string;
  userName: string;
  email: string;
  phoneNumber?: string;
  roleId: string;
  companyId?: string;
  isActive?: boolean;
}

export interface UpdateUserDto {
  userName?: string;
  email?: string;
  phoneNumber?: string;
  roleId?: string;
  companyId?: string;
  isActive?: boolean;
}

export interface CompanyDto {
  id: string;
  name: string;
  address?: string;
  companyDomain?: string;
  logo?: string;
  phoneNumber?: string;
  description?: string;
  email?: string;
  active: boolean;
}

let userCompanyCache: {
  data: CompanyDto | null;
  timestamp: number;
} | null = null;

const COMPANY_CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

const isCacheValid = (): boolean => {
  if (!userCompanyCache) return false;
  return Date.now() - userCompanyCache.timestamp < COMPANY_CACHE_TTL;
};

export const clearUserCompanyCache = (): void => {
  userCompanyCache = null;
};

export const isCachedUserCompanyValid = (): boolean => {
  if (!userCompanyCache) return false;
  return Date.now() - userCompanyCache.timestamp < COMPANY_CACHE_TTL;
};

export const getCachedUserCompany = (): CompanyDto | null => {
  if (isCachedUserCompanyValid() && userCompanyCache?.data) {
    return userCompanyCache.data;
  }
  return null;
};

// User specific API calls
export const userService = {
  // Get all users (optionally filtered by companyId)
  getAll: (companyId?: string) => {
    const params = new URLSearchParams();
    if (companyId && companyId !== "all") {
      params.append("companyId", companyId);
    }
    const queryString = params.toString();
    return baseApiService.get<UserResponse[]>(`/User${queryString ? `?${queryString}` : ""}`);
  },

  // Get user by ID
  getById: (id: string) => baseApiService.get<UserResponse>(`/User/${id}`),

  // Create new user
  create: (dto: CreateUserDto) =>
    baseApiService.post<UserResponse>("/User", dto),

  // Update user
  update: (id: string, dto: UpdateUserDto) =>
    baseApiService.put<UserResponse>(`/User/${id}`, dto),

  // Update user active status
  updateStatus: (id: string, dto: UpdateUserStatusDto) =>
    baseApiService.patch<void>(`/User/${id}/status`, dto),

  // Delete user
  delete: (id: string) => baseApiService.delete<void>(`/User/${id}`),

  // Get current user profile
  getCurrentUser: () => baseApiService.get<UserResponse>(`/User/Profile`),
  
  // Get current user company
  getCurrentUserCompany: () => baseApiService.get<CompanyDto>(`/User/Company`),
};
